import { Users as UsersIcon } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { adminApi } from "@/client/services/admin";

const PAGE_SIZE = 20;

export const AdminUsersPage = () => {
  const [search, setSearch] = useState<string>("");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-users", search, subscriptionStatus, offset],
    queryFn: () =>
      adminApi.getUsers({
        search: search || undefined,
        subscriptionStatus: subscriptionStatus || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Admin Users - VillageHut Resume</title>
      </Helmet>

      <h1 className="text-2xl font-semibold">Users</h1>

      <div className="flex flex-wrap items-center gap-4">
        <Input
          placeholder="Search by name or email"
          className="max-w-sm"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <Select
          value={subscriptionStatus || "all"}
          onValueChange={(v) => {
            setSubscriptionStatus(v === "all" ? "" : v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Subscription status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="size-5" />
            User list
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-muted-foreground">Loading...</p>}
          {error && <p className="text-destructive">Failed to load users.</p>}
          {!isLoading && !error && users.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">No users found.</div>
          )}
          {!isLoading && !error && users.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Joined</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Subscription</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Resumes</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Total spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(users ?? [])
                      .filter((u: unknown): u is Record<string, unknown> => u != null)
                      .map((u: Record<string, unknown>) => (
                        <tr key={String(u.id)} className="border-b">
                          <td className="px-4 py-3 text-sm font-medium">
                            {String((u as { name?: string }).name ?? "—")}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {String((u as { email?: string }).email ?? "—")}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {dayjs((u as { createdAt?: string }).createdAt).format("MMM D, YYYY")}
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const sub = u.subscription as
                                | { status?: string | null; planType?: string | null }
                                | null
                                | undefined;
                              const statusVal = sub == null ? undefined : sub.status;
                              const planVal = sub == null ? undefined : sub.planType;
                              if (statusVal) {
                                return (
                                  <Badge variant="secondary">
                                    {planVal ?? "—"} / {statusVal}
                                  </Badge>
                                );
                              }
                              return <span className="text-muted-foreground">None</span>;
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm">{Number(u.resumeCount ?? 0)}</td>
                          <td className="px-4 py-3 text-sm font-medium">
                            KES {Number(u.totalSpent ?? 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => {
                        setPage((p) => p - 1);
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => {
                        setPage((p) => p + 1);
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
